import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

export interface QuestionMarkConfig {
  controlId: string;  // The lowercase control identifier (e.g., "prioritize", "google")
  helpText: string;   // The explanatory text to display
}

/**
 * Parse a raw questionmark string into a structured config
 * Format: "<controlId>help text here"
 * Example: "<prioritize>defines peer reviewers; tradeoffs: intelligence smartest..."
 * 
 * Note: Items starting with <_script_*> are goal-tracking scripts and are
 * filtered out from the UI questionmarks system (handled server-side)
 */
function parseQuestionMarkEntry(raw: string): QuestionMarkConfig | null {
  const match = raw.match(/^<([^>]+)>(.+)$/s);
  if (!match) {
    console.warn('⚠️ Invalid questionmark format:', raw.substring(0, 50));
    return null;
  }
  
  const controlId = match[1].toLowerCase().trim();
  
  // Filter out _script_* items - these are goal-tracking scripts, not UI help
  if (controlId.startsWith('_script_')) {
    return null;
  }
  
  return {
    controlId,
    helpText: match[2].trim()
  };
}

export class QuestionMarkService {
  private static cache: QuestionMarkConfig[] | null = null;
  private static cacheTimestamp: number = 0;
  private static CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  /**
   * Fetch all questionmark configs from Firestore
   * Questionmarks are stored as an array in system/config document
   * Format: ["<controlId>help text", "<controlId2>help text 2", ...]
   */
  static async getQuestionMarks(): Promise<QuestionMarkConfig[]> {
    // Return cached data if still valid
    const now = Date.now();
    if (this.cache && (now - this.cacheTimestamp) < this.CACHE_TTL_MS) {
      return this.cache;
    }

    try {
      console.log('🔍 Fetching questionmarks from Firestore...');

      const configRef = doc(db, 'system', 'config');
      const configSnap = await getDoc(configRef);

      if (!configSnap.exists()) {
        console.warn('⚠️ System config document not found in Firestore');
        return [];
      }

      const data = configSnap.data();
      const rawQuestionmarks = data.questionmarks as string[] || [];

      if (rawQuestionmarks.length === 0) {
        console.warn('⚠️ No questionmarks found in system config');
        return [];
      }

      // Parse each raw entry into structured config
      const configs = rawQuestionmarks
        .map(parseQuestionMarkEntry)
        .filter((config): config is QuestionMarkConfig => config !== null);

      console.log(`📊 Retrieved ${configs.length} questionmarks from Firestore`);
      
      // Update cache
      this.cache = configs;
      this.cacheTimestamp = now;
      
      return configs;
    } catch (error) {
      console.error('❌ Error fetching questionmarks from Firestore:', error);
      return [];
    }
  }

  /**
   * Get help text for a specific control by its display label
   * Matches case-insensitively against the controlId
   */
  static async getHelpForControl(controlLabel: string): Promise<string | null> {
    const configs = await this.getQuestionMarks();
    const normalizedLabel = controlLabel.toLowerCase().trim();
    
    const config = configs.find(c => c.controlId === normalizedLabel);
    return config?.helpText ?? null;
  }

  /**
   * Clear the cache (useful for testing or after config updates)
   */
  static clearCache(): void {
    this.cache = null;
    this.cacheTimestamp = 0;
  }
}

